// Small phone building blocks: bottom sheet, pull-to-refresh, list rows.
import { useRef, useState } from "react";
import { ChevronRight, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export const TOPBAR_OFFSET = "calc(3rem + env(safe-area-inset-top, 0px))";

export function BottomSheet({ open, onClose, title, children, footer, testid, className = "" }) {
  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent aria-describedby={undefined} data-testid={testid} className={className}>
        {title && (
          <DialogHeader className="text-left">
            <DialogTitle className="text-base pr-10">{title}</DialogTitle>
          </DialogHeader>
        )}
        <div className="space-y-3 min-w-0 max-w-full overflow-x-hidden">{children}</div>
        {footer}
      </DialogContent>
    </Dialog>
  );
}

export function PullToRefresh({ onRefresh, children, className = "" }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef(null);
  const onStart = (e) => { if (window.scrollY > 0) return; start.current = e.touches[0].clientY; };
  const onMove = (e) => {
    if (start.current == null || busy) return;
    const d = e.touches[0].clientY - start.current;
    if (d > 0 && window.scrollY === 0) setPull(Math.min(96, d * 0.5));
  };
  const onEnd = async () => {
    if (start.current == null) return;
    start.current = null;
    if (pull >= 56 && !busy) {
      setBusy(true);
      try { await onRefresh?.(); } finally { setBusy(false); }
    }
    setPull(0);
  };
  return (
    <div onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} className={className} data-testid="pull-to-refresh">
      <div style={{ height: busy ? 40 : pull }} className="flex items-center justify-center overflow-hidden transition-[height] duration-150 text-slate-500">
        {(busy || pull > 0) && <RefreshCw className={cn("w-4 h-4", busy && "animate-spin")} style={busy ? undefined : { transform: `rotate(${pull * 3}deg)` }} />}
      </div>
      {children}
    </div>
  );
}

export function SheetRow({ icon: Icon, label, sub, badge, onClick, testid, disabled = false, tone = "" }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={cn("w-full flex items-center gap-3 min-h-[48px] px-3 rounded-lg text-left sf-tap active:bg-slate-100 disabled:opacity-40", tone)}
      data-testid={testid}>
      {Icon && <Icon className="w-5 h-5 shrink-0 text-slate-500" />}
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] truncate">{label}</span>
        {sub && <span className="block text-xs text-slate-500 truncate">{sub}</span>}
      </span>
      {badge > 0 && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{badge > 99 ? "99+" : badge}</span>}
      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
    </button>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between px-1 pt-1">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{children}</div>
      {action}
    </div>
  );
}
